/**
 * ============================================================================
 * QualificationChain — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: visual qualification chain (URS → DQ → FAT → SAT → IQ → OQ → PQ →
 *          REQUAL). Rendered inline as a single SVG so we avoid pulling in
 *          a chart library. Each step is a labelled node:
 *            PASSED  → filled green circle with check
 *            FAILED  → filled red circle with X
 *            CURRENT → filled blue circle (open ring)
 *            PENDING → gray dot
 *            MISSING → empty dotted ring
 *          A detail table follows so signers and document ids are visible.
 *
 * A11Y: each node has aria-label; legend reuses the same labels; the SVG
 *  itself is role="img" with a descriptive group title.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentDetail.module.css';
import type {
  QualificationStep,
  QualificationStepKey,
  QualificationStepState,
} from './useEquipmentDetailQuery';

const STEP_ORDER: QualificationStepKey[] = [
  'URS',
  'DQ',
  'FAT',
  'SAT',
  'IQ',
  'OQ',
  'PQ',
  'REQUAL',
];

const STATE_FILL: Record<QualificationStepState, string> = {
  PASSED: '#1DD1A1',
  FAILED: '#EF4444',
  CURRENT: '#2563EB',
  PENDING: '#9CA3AF',
  MISSING: 'transparent',
};

const STATE_STROKE: Record<QualificationStepState, string> = {
  PASSED: '#0E9F7E',
  FAILED: '#B91C1C',
  CURRENT: '#1E40AF',
  PENDING: '#6B7280',
  MISSING: '#9CA3AF',
};

export interface QualificationChainProps {
  steps: QualificationStep[];
}

export function QualificationChain({ steps }: QualificationChainProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  // Build a map so we can render in the canonical order even if the server
  // sends steps out of order.
  const stepMap = useMemo(() => {
    const m = new Map<QualificationStepKey, QualificationStep>();
    for (const s of steps) m.set(s.key, s);
    return m;
  }, [steps]);

  const w = 720;
  const h = 110;
  const padX = 40;
  const gap = (w - padX * 2) / (STEP_ORDER.length - 1);
  const r = 18;

  return (
    <div className={styles.chainWrap}>
      <div className={styles.chainScroll}>
        <svg
          className={styles.chainSvg}
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label={t('equipment.detail.qualifications.chainAria', {
            defaultValue:
              'Qualification chain: URS, DQ, FAT, SAT, IQ, OQ, PQ, REQUAL.',
          })}
        >
          <title>
            {t('equipment.detail.qualifications.chainAria', {
              defaultValue:
                'Qualification chain: URS, DQ, FAT, SAT, IQ, OQ, PQ, REQUAL.',
            })}
          </title>
          {/* Connector line */}
          <line
            x1={padX}
            y1={h / 2}
            x2={w - padX}
            y2={h / 2}
            stroke="#E5E7EB"
            strokeWidth={2}
          />
          {STEP_ORDER.map((key, idx) => {
            const step = stepMap.get(key);
            const state: QualificationStepState = step?.state ?? 'MISSING';
            const cx = padX + idx * gap;
            const cy = h / 2;
            const fill = STATE_FILL[state];
            const stroke = STATE_STROKE[state];
            const isFilled =
              state === 'PASSED' || state === 'FAILED' || state === 'CURRENT';
            const stateLabel = t(`equipment.detail.qualifications.state.${state}`, {
              defaultValue: state,
            });
            return (
              <g key={key}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={isFilled ? fill : '#fff'}
                  stroke={stroke}
                  strokeWidth={2}
                  strokeDasharray={state === 'MISSING' ? '3 3' : undefined}
                >
                  <title>{`${key} — ${stateLabel}`}</title>
                </circle>
                {state === 'PASSED' && (
                  <path
                    d={`M ${cx - 7} ${cy} l 5 5 l 10 -10`}
                    stroke="#fff"
                    strokeWidth={2.5}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {state === 'FAILED' && (
                  <g
                    stroke="#fff"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                  >
                    <line x1={cx - 6} y1={cy - 6} x2={cx + 6} y2={cy + 6} />
                    <line x1={cx + 6} y1={cy - 6} x2={cx - 6} y2={cy + 6} />
                  </g>
                )}
                {state === 'CURRENT' && (
                  <circle cx={cx} cy={cy} r={6} fill="#fff" />
                )}
                <text
                  x={cx}
                  y={cy + r + 18}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="var(--color-text)"
                >
                  {key}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div
        className={styles.chainLegend}
        aria-label={t('equipment.detail.qualifications.legendAria', {
          defaultValue: 'Qualification step legend',
        })}
      >
        {(
          ['PASSED', 'FAILED', 'CURRENT', 'PENDING', 'MISSING'] as QualificationStepState[]
        ).map((s) => (
          <span key={s} className={styles.chainLegendItem}>
            <span
              className={styles.dot}
              style={{
                background: s === 'MISSING' ? 'transparent' : STATE_FILL[s],
                border: `1px solid ${STATE_STROKE[s]}`,
              }}
              aria-hidden="true"
            />
            {t(`equipment.detail.qualifications.state.${s}`, {
              defaultValue: s,
            })}
          </span>
        ))}
      </div>

      <table
        className={styles.chainTable}
        aria-label={t('equipment.detail.qualifications.tableAria', {
          defaultValue: 'Qualification chain — detailed status per step',
        })}
      >
        <caption className={styles.srOnly}>
          {t('equipment.detail.qualifications.tableCaption', {
            defaultValue:
              'Per-step state, signer, signed-at, and the linked qualification document id.',
          })}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {t('equipment.detail.qualifications.columns.step', {
                defaultValue: 'Step',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.qualifications.columns.state', {
                defaultValue: 'State',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.qualifications.columns.signedAt', {
                defaultValue: 'Signed at',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.qualifications.columns.signer', {
                defaultValue: 'Signer',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.qualifications.columns.document', {
                defaultValue: 'Document',
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          {STEP_ORDER.map((key) => {
            const step = stepMap.get(key);
            const state: QualificationStepState = step?.state ?? 'MISSING';
            return (
              <tr key={key}>
                <td>
                  <strong>{key}</strong>
                </td>
                <td>
                  {t(`equipment.detail.qualifications.state.${state}`, {
                    defaultValue: state,
                  })}
                </td>
                <td>
                  {step?.signed_at_utc ? (
                    <time dateTime={step.signed_at_utc}>
                      {dateFmt.format(new Date(step.signed_at_utc))}
                    </time>
                  ) : (
                    <span className={styles.subdued}>—</span>
                  )}
                </td>
                <td>
                  {step?.signer ?? <span className={styles.subdued}>—</span>}
                </td>
                <td>
                  {step?.document_id ? (
                    <code className={styles.codeChip}>{step.document_id}</code>
                  ) : (
                    <span className={styles.subdued}>—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
